"use client";

import { useMemo, useState } from "react";

import {
  FilterButton,
  type ActiveFilter,
} from "@/components/ui/filter-panel";

import { useConversationColumns } from "../../hooks/use-conversation-columns";
import {
  activeFiltersToFilterIn,
  columnsToFilterFields,
  filterInToActiveFilters,
} from "../../lib/filter-map";
import { useListFilterParam } from "./use-list-filters";

/**
 * The jobs list's `FilterButton`, driven by the meeting report columns.
 * The panel keeps its own `ActiveFilter[]` draft because a freshly added row
 * has no value yet and therefore maps to nothing in the URL — reading the
 * chips straight back from `f` would make them vanish as they are created.
 */
export function ConversationsFilterButton() {
  const columns = useConversationColumns();
  const registry = columns.data;
  const { filters, setFilters } = useListFilterParam();
  const [open, setOpen] = useState(false);

  const fields = useMemo(
    () => (registry ? columnsToFilterFields(registry) : []),
    [registry],
  );
  const fromUrl = useMemo(
    () => (registry ? filterInToActiveFilters(registry, filters) : []),
    [registry, filters],
  );

  const urlKey = `${registry?.version ?? ""}:${JSON.stringify(filters)}`;
  // `seen` is the `f` param this component last reconciled with; `written` is
  // the one it produced itself. A change we didn't write (back button, a link
  // with filters, the empty-state's "Clear all filters") re-seeds the chips;
  // our own writes leave the draft alone so an in-progress row survives.
  const [sync, setSync] = useState({
    seen: urlKey,
    written: null as string | null,
  });
  const [draft, setDraft] = useState<ActiveFilter[]>(fromUrl);
  if (sync.seen !== urlKey) {
    setSync((prev) => ({ ...prev, seen: urlKey }));
    if (urlKey !== sync.written) setDraft(fromUrl);
  }

  if (!registry || fields.length === 0) return null;

  return (
    <FilterButton
      fields={fields}
      filters={draft}
      onFiltersChange={(next) => {
        setDraft(next);
        const mapped = activeFiltersToFilterIn(registry, next);
        const key = `${registry.version}:${JSON.stringify(mapped)}`;
        if (key === urlKey) return;
        setSync((prev) => ({ ...prev, written: key }));
        setFilters(mapped);
      }}
      open={open}
      onOpenChange={setOpen}
    />
  );
}
